import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send to our backend API which will handle Web3Forms and database storage
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          message: formData.message,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Děkujeme! Vaši poptávku jsme obdrželi. Brzy se vám ozveme.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        toast.error('Omlouváme se, něco se pokazilo. Zkuste to prosím znovu nebo nás kontaktujte telefonicky.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Omlouváme se, něco se pokazilo. Zkuste to prosím znovu nebo nás kontaktujte telefonicky.');
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="heading-2 mb-6">
            KONTAKTUJTE NÁS
          </h2>
          <p className="body-medium text-text-secondary max-w-3xl mx-auto">
            Nezávazná kalkulace zdarma • Rychlé jednání • Profesionální přístup
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="heading-4 mb-8">
                Spojte se s námi
              </h3>
              <p className="body-medium text-text-secondary mb-8">
                Rádi si s vámi probereme váš projekt a připravíme nezávaznou cenovou nabídku.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 p-6 bg-bg-page border border-border-light rounded-lg transition-all duration-300 hover:border-brand-primary">
                <div className="bg-red-50 p-3 rounded-lg shrink-0 border border-red-200">
                  <Phone style={{ color: '#dc143c' }} size={24} strokeWidth={2.5} />
                </div>
                <div>
                  <div className="caption mb-1">Telefon</div>
                  <a 
                    href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                    className="heading-6 text-brand-primary hover:text-brand-hover transition-colors"
                  >
                    {companyInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-bg-page border border-border-light rounded-lg transition-all duration-300 hover:border-brand-primary">
                <div className="bg-red-50 p-3 rounded-lg shrink-0 border border-red-200">
                  <Mail style={{ color: '#dc143c' }} size={24} strokeWidth={2.5} />
                </div>
                <div>
                  <div className="caption mb-1">Email</div>
                  <a 
                    href={`mailto:${companyInfo.email}`}
                    className="heading-6 text-brand-primary hover:text-brand-hover transition-colors break-all"
                  >
                    {companyInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-bg-page border border-border-light rounded-lg transition-all duration-300 hover:border-brand-primary">
                <div className="bg-red-50 p-3 rounded-lg shrink-0 border border-red-200">
                  <MapPin style={{ color: '#dc143c' }} size={24} strokeWidth={2.5} />
                </div>
                <div>
                  <div className="caption mb-1">Adresa</div>
                  <div className="heading-6">
                    {companyInfo.address}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-bg-page border border-border-light rounded-lg p-8">
            <h3 className="heading-5 mb-6">
              Pošlete nám poptávku
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="caption mb-2 block">
                  Jméno a příjmení *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-white border-border-medium focus:border-brand-primary"
                  placeholder="Vaše jméno"
                />
              </div>

              <div>
                <label htmlFor="email" className="caption mb-2 block">
                  Email *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-white border-border-medium focus:border-brand-primary"
                  placeholder="vas@email.cz"
                />
              </div>

              <div>
                <label htmlFor="phone" className="caption mb-2 block">
                  Telefon
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-white border-border-medium focus:border-brand-primary"
                  placeholder="Vaše telefonní číslo"
                />
              </div>

              <div>
                <label htmlFor="message" className="caption mb-2 block">
                  Zpráva *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="bg-white border-border-medium focus:border-brand-primary resize-none"
                  placeholder="Popište nám váš projekt..."
                />
              </div>

              <Button 
                type="submit"
                className="btn-primary w-full text-lg py-6"
              >
                Odeslat poptávku
              </Button>

              <p className="caption text-center">
                * Povinná pole
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
